// ─────────────────────────────────────────────
//  Facility Settings tab — multi-facility configuration.
//
//  Top: a facility dropdown selects which facility this page configures
//  (defaults to the user's active facility). Facility-specific sections
//  (info, locations, surgeons, specialties) follow that selection; the staff
//  pool is shared across all facilities, and the status section lists every
//  known facility so an admin can activate/deactivate the empty shells.
//
//  Staff panel lives in setupPage.roster.js, the Surgeons panel in
//  setupPage.surgeons.js and the Facility Status list in
//  setupPage.facilityStatus.js (file-size split).
// ─────────────────────────────────────────────
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { updateFacility, updateFacilityLocations } from "../firebase/firestore";
import { SPECIALTY_ROOM_OPTIONS } from "../domain/facilityRules";
import { FacilityRulesPanel } from "./setupPage.rules";
import { CoverageMapPanel } from "./setupPage.coverageMap";
import { useFacility } from "../hooks/useFacility";
import { useLocations } from "../hooks/useLocations";
import { slugify as slugLoc } from "../domain/locations";
import {
  resolveSpecialties, slugify as slugSpec, sortSpecialties,
} from "../domain/specialties";
import { facilityLabel } from "../domain/facilities";
import tokens from "../theme/tokens";
import s from "./setupPage.styles";
import { StaffPanel } from "./setupPage.roster";
// The surgeon roster moved out of setupPage.roster.js when it stopped being an
// array field on the scheduling doc and became the `surgeons` collection the
// Team page reads. See that file's header for which list is authoritative.
import { SurgeonsPanel } from "./setupPage.surgeons";
import { SheetImportConfigPanel } from "./setupPage.sheetImport";
import { FacilityStatusPanel } from "./setupPage.facilityStatus";
import { isSandboxBoot } from "../sandbox/mode";

const { color } = tokens;

// ── Orchestrator ──────────────────────────────
// facilityId is owned by SetupPage (shared selector in the topbar) so the
// Facility and Rooms tabs configure the same facility.
export function FacilityTab({ facilityId }) {
  return (
    <div>
      <div style={s.tabHeader}>
        <div>
          <div style={s.tabTitle}>Facility Settings</div>
          <div style={s.tabSub}>Configure each hospital or surgical center.</div>
        </div>
      </div>

      <FacilityInfoPanel facilityId={facilityId} />
      <FacilityRulesPanel facilityId={facilityId} />
      <CoverageMapPanel facilityId={facilityId} />
      {/* Sheet import reaches Google. The sandbox makes no external
          connections, so the panel is hidden rather than shown broken. */}
      {!isSandboxBoot() && <SheetImportConfigPanel facilityId={facilityId} />}
      <LocationsPanel facilityId={facilityId} />
      <SurgeonsPanel facilityId={facilityId} />
      <SpecialtiesPanel facilityId={facilityId} />
      <StaffPanel />
      <FacilityStatusPanel />
    </div>
  );
}

// ── Facility information (persisted) ──────────
//
// ⚠️ THE BUILDING'S OWN LINE AND A NAMED PERSON'S LINE ARE TWO DIFFERENT FACTS.
// `contact` / `chief` / `viceChief` below hold a PERSON — their name, their work
// email, their phone. `phoneNumber` and `email` (the Main phone / General email
// pair added to the top grid) hold the FACILITY: the switchboard a clinician
// dials at 03:00 when they do not know who is on, and the department inbox.
//
// The Team page shows a facility row and reads `phoneNumber` / `email` first,
// falling back to `contact` (domain/contact.js). Before these two inputs
// existed NOTHING in the app wrote them, so the field the directory reads could
// never be filled — a form the UI cannot populate is a dead binding with extra
// steps. If a facility has only a contact person, the row still renders; that
// is the fallback, not the design.
//
// `phoneDisplay` is deliberately NOT collected: one input, one stored value.
// What is typed here is what is shown, and telHref() strips the punctuation
// before dialing, so a second "what a human reads" field would only be a second
// place for the same number to go stale.
const CONTACT_GROUPS = [
  { key: "contact",   title: "Contact Person",         required: true },
  { key: "chief",     title: "Chief of Anesthesia",    required: false },
  { key: "viceChief", title: "Vice Chief of Anesthesia", required: false },
];

function FacilityInfoPanel({ facilityId }) {
  const { isAdmin } = useAuth();
  const toast = useToast();
  const { facility, loading } = useFacility(facilityId);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  // Seed the form from the live doc whenever the facility changes.
  useEffect(() => {
    setForm({
      name: facility?.name || facilityLabel(facilityId),
      address: facility?.address || "",
      // The facility's OWN switchboard + inbox — see the note above.
      phoneNumber: facility?.phoneNumber || "",
      email: facility?.email || "",
      contact: { name: "", email: "", phone: "", ...(facility?.contact || {}) },
      chief: { name: "", email: "", phone: "", ...(facility?.chief || {}) },
      viceChief: { name: "", email: "", phone: "", ...(facility?.viceChief || {}) },
      // Phase 5.4 profile (SC1): OR count + specialty rooms.
      orCount: facility?.orCount ?? "",
      specialtyRooms: Array.isArray(facility?.specialtyRooms) ? facility.specialtyRooms : [],
    });
  }, [facility, facilityId]);

  if (loading || !form) {
    return <div style={s.panel}><div style={{ fontSize: 13, color: color.text.muted }}>Loading facility…</div></div>;
  }

  const setField = (path, value) => {
    setForm((prev) => {
      if (!path.includes(".")) return { ...prev, [path]: value };
      const [grp, sub] = path.split(".");
      return { ...prev, [grp]: { ...prev[grp], [sub]: value } };
    });
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Facility name is required."); return; }
    if (!form.address.trim()) { toast.error("Address is required."); return; }
    if (!form.contact.name.trim() || !form.contact.email.trim() || !form.contact.phone.trim()) {
      toast.error("Contact person name, email, and phone are required.");
      return;
    }
    setSaving(true);
    try {
      await updateFacility(facilityId, {
        name: form.name.trim(),
        address: form.address.trim(),
        phoneNumber: form.phoneNumber.trim(),
        email: form.email.trim(),
        contact: form.contact,
        chief: form.chief,
        viceChief: form.viceChief,
        orCount: form.orCount === "" ? null : Math.max(0, parseInt(form.orCount, 10) || 0),
        specialtyRooms: form.specialtyRooms,
      });
      toast.success("Facility information saved.");
    } catch (err) {
      console.error("Save facility failed:", err);
      toast.error("Could not save. Admin rights are required.");
    } finally {
      setSaving(false);
    }
  };

  const req = (txt) => (
    <>{txt} <span style={{ color: color.accent.error }} aria-hidden="true">*</span></>
  );

  return (
    <div style={s.panel}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div style={s.panelTitle}>🏥 Facility Information</div>
        {isAdmin && (
          <button style={s.primaryBtn} onClick={save} disabled={saving}>
            {saving ? "Saving…" : "💾 Save Changes"}
          </button>
        )}
      </div>

      <div style={s.formGrid}>
        <div style={s.formGroup}>
          <label style={s.label}>{req("Facility Name")}</label>
          <input style={s.input} value={form.name} disabled={!isAdmin}
            onChange={(e) => setField("name", e.target.value)} />
        </div>
        <div style={s.formGroup}>
          <label style={s.label} htmlFor="facility-address">{req("Address")}</label>
          <input id="facility-address" style={s.input} value={form.address} disabled={!isAdmin}
            onChange={(e) => setField("address", e.target.value)} />
        </div>
        <div style={s.formGroup}>
          <label style={s.label} htmlFor="facility-phone">Main phone</label>
          <input id="facility-phone" style={s.input} type="tel" placeholder="(813) 555-0100"
            value={form.phoneNumber} disabled={!isAdmin}
            onChange={(e) => setField("phoneNumber", e.target.value)} />
        </div>
        <div style={s.formGroup}>
          <label style={s.label} htmlFor="facility-email">General email</label>
          <input id="facility-email" style={s.input} type="email" placeholder="or.desk@example.org"
            value={form.email} disabled={!isAdmin}
            onChange={(e) => setField("email", e.target.value)} />
        </div>
      </div>
      <p style={{ fontSize: 12, color: color.text.muted, marginTop: 8, marginBottom: 0 }}>
        The address, main phone and general email are what the Team page shows for
        this facility. Leave the phone or email blank and it falls back to the
        contact person below.
      </p>

      {/* Phase 5.4 profile row (SC1): OR count + specialty rooms. */}
      <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "160px 1fr", gap: 12, alignItems: "start" }}>
        <div style={s.formGroup}>
          <label style={s.label} htmlFor="facility-or-count">OR Count</label>
          <input id="facility-or-count" style={s.input} type="number" min="0" max="99"
            value={form.orCount} disabled={!isAdmin}
            onChange={(e) => setField("orCount", e.target.value)} />
        </div>
        <div style={s.formGroup}>
          <span style={s.label}>Specialty Rooms</span>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", paddingTop: 8 }}>
            {SPECIALTY_ROOM_OPTIONS.map((opt) => (
              <label key={opt} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: color.text.primary }}>
                <input
                  type="checkbox"
                  disabled={!isAdmin}
                  checked={form.specialtyRooms.includes(opt)}
                  onChange={(e) =>
                    setField(
                      "specialtyRooms",
                      e.target.checked
                        ? [...form.specialtyRooms, opt]
                        : form.specialtyRooms.filter((x) => x !== opt)
                    )
                  }
                />
                {opt}
              </label>
            ))}
          </div>
        </div>
      </div>

      {CONTACT_GROUPS.map((g) => (
        <div key={g.key} style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: color.text.secondary, marginBottom: 8 }}>
            {g.title}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            {["name", "email", "phone"].map((sub) => (
              <div key={sub} style={s.formGroup}>
                <label style={s.label}>
                  {g.required ? req(sub[0].toUpperCase() + sub.slice(1)) : sub[0].toUpperCase() + sub.slice(1)}
                </label>
                <input style={s.input} value={form[g.key][sub]} disabled={!isAdmin}
                  type={sub === "email" ? "email" : sub === "phone" ? "tel" : "text"}
                  onChange={(e) => setField(`${g.key}.${sub}`, e.target.value)} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── OR Locations (facility-specific) ──────────
// Moved from SetupPage.js, now parametrized by facilityId so it tracks the
// dropdown selection rather than only the user's active facility.
function LocationsPanel({ facilityId }) {
  const { isAdmin } = useAuth();
  const toast = useToast();
  const { locations } = useLocations(facilityId);
  const [busy, setBusy] = useState(false);
  const [editKey, setEditKey] = useState(null);
  const [editLabel, setEditLabel] = useState("");

  const persist = async (next) => {
    if (!isAdmin) { toast.error("Only an administrator can change locations."); return; }
    setBusy(true);
    try {
      await updateFacilityLocations(facilityId, next);
    } catch (err) {
      console.error("Save locations failed:", err);
      toast.error("Could not save locations.");
    } finally {
      setBusy(false);
    }
  };

  const toggle = (key) =>
    persist(locations.map((l) => (l.key === key ? { ...l, active: l.active === false } : l)));

  const addCustom = async () => {
    const label = window.prompt("New location name (e.g. Hybrid OR, L&D)")?.trim();
    if (!label) return;
    const key = slugLoc(label);
    if (locations.some((l) => l.key === key)) { toast.error(`"${label}" already exists.`); return; }
    const otherIdx = locations.findIndex((l) => l.key === "other");
    const entry = { key, label, active: true, custom: true };
    const next = otherIdx === -1
      ? [...locations, entry]
      : [...locations.slice(0, otherIdx), entry, ...locations.slice(otherIdx)];
    await persist(next);
    toast.success(`"${label}" added.`);
  };

  const startEdit = (loc) => { setEditKey(loc.key); setEditLabel(loc.label); };
  const saveEdit = async () => {
    const label = editLabel.trim();
    if (!label) return;
    await persist(locations.map((l) => (l.key === editKey ? { ...l, label } : l)));
    setEditKey(null);
  };
  const removeCustom = async (loc) => {
    if (!window.confirm(`Remove "${loc.label}"? Rooms already set to it keep their label until reassigned.`)) return;
    await persist(locations.filter((l) => l.key !== loc.key));
  };

  return (
    <div style={s.panel}>
      <div style={{ ...s.panelTitle, marginBottom: 12 }}>📍 OR Locations</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
        {locations.map((loc) => (
          editKey === loc.key ? (
            <span key={loc.key} style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
              <input style={{ ...s.input, width: 150, padding: "5px 10px" }} value={editLabel}
                onChange={(e) => setEditLabel(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") setEditKey(null); }} autoFocus />
              <button style={{ ...s.ghostBtn, color: color.accent.active, fontWeight: 700 }} onClick={saveEdit}>✓</button>
              <button style={s.ghostBtn} onClick={() => setEditKey(null)}>✕</button>
            </span>
          ) : (
            <span key={loc.key} style={{ ...s.locChip, ...(loc.active !== false ? s.locActive : {}),
              display: "inline-flex", alignItems: "center", gap: 8, cursor: "default", opacity: busy ? 0.6 : 1 }}>
              <button style={{ background: "transparent", border: "none", color: "inherit", cursor: "pointer", fontFamily: "inherit", fontSize: 12, padding: 0 }}
                aria-pressed={loc.active !== false}
                title={loc.active !== false ? "Active — click to deactivate" : "Inactive — click to activate"}
                disabled={!isAdmin || busy} onClick={() => toggle(loc.key)}>
                {loc.active !== false ? "● " : "○ "}{loc.label}
              </button>
              {isAdmin && (
                <button style={{ background: "transparent", border: "none", color: color.text.secondary, cursor: "pointer", fontSize: 11, padding: 0 }}
                  title="Rename" onClick={() => startEdit(loc)}>✎</button>
              )}
              {isAdmin && loc.key !== "other" && (
                <button style={{ background: "transparent", border: "none", color: color.accent.error, cursor: "pointer", fontSize: 12, padding: 0 }}
                  title="Remove" onClick={() => removeCustom(loc)}>×</button>
              )}
            </span>
          )
        ))}
        {isAdmin && (
          <button style={s.locAdd} onClick={addCustom} disabled={busy}>＋ Add Custom</button>
        )}
      </div>
      <p style={{ fontSize: 12, color: color.text.muted }}>
        Active locations (●) appear in room setup and on the board. {isAdmin ? "Click a location to toggle it, ✎ to rename, × to remove." : "Only an administrator can edit locations."}
      </p>
    </div>
  );
}

// ── Specialties (facility-specific) ───────────
function SpecialtiesPanel({ facilityId }) {
  const { isAdmin } = useAuth();
  const toast = useToast();
  const { facility } = useFacility(facilityId);
  const specialties = useMemo(() => resolveSpecialties(facility?.specialties), [facility]);
  const [busy, setBusy] = useState(false);
  const [editKey, setEditKey] = useState(null);
  const [editLabel, setEditLabel] = useState("");

  const persist = async (next) => {
    if (!isAdmin) { toast.error("Only an administrator can change specialties."); return; }
    setBusy(true);
    try {
      await updateFacility(facilityId, { specialties: next });
    } catch (err) {
      console.error("Save specialties failed:", err);
      toast.error("Could not save specialties.");
    } finally {
      setBusy(false);
    }
  };

  const toggle = (key) =>
    persist(specialties.map((sp) => (sp.key === key ? { ...sp, active: sp.active === false } : sp)));

  const addCustom = async () => {
    const label = window.prompt("New specialty (e.g. Plastic Surgery)")?.trim();
    if (!label) return;
    const key = slugSpec(label);
    if (specialties.some((sp) => sp.key === key)) { toast.error(`"${label}" already exists.`); return; }
    const entry = { key, label, active: true, custom: true };
    // Slot the new entry into alphabetical order (Other stays last).
    await persist(sortSpecialties([...specialties, entry]));
    toast.success(`"${label}" added.`);
  };

  const startEdit = (sp) => { setEditKey(sp.key); setEditLabel(sp.label); };
  const saveEdit = async () => {
    const label = editLabel.trim();
    if (!label) return;
    // Re-sort after a rename so the list stays alphabetical.
    await persist(sortSpecialties(specialties.map((sp) => (sp.key === editKey ? { ...sp, label } : sp))));
    setEditKey(null);
  };
  const removeCustom = async (sp) => {
    if (!window.confirm(`Remove "${sp.label}"? Surgeons set to it move to Other.`)) return;
    await persist(specialties.filter((s2) => s2.key !== sp.key));
  };

  return (
    <div style={s.panel}>
      <div style={{ ...s.panelTitle, marginBottom: 12 }}>🏷️ Specialties</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
        {specialties.map((sp) => (
          editKey === sp.key ? (
            <span key={sp.key} style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
              <input style={{ ...s.input, width: 160, padding: "5px 10px" }} value={editLabel}
                onChange={(e) => setEditLabel(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") setEditKey(null); }} autoFocus />
              <button style={{ ...s.ghostBtn, color: color.accent.active, fontWeight: 700 }} onClick={saveEdit}>✓</button>
              <button style={s.ghostBtn} onClick={() => setEditKey(null)}>✕</button>
            </span>
          ) : (
            <span key={sp.key} style={{ ...s.locChip, ...(sp.active !== false ? s.locActive : {}),
              display: "inline-flex", alignItems: "center", gap: 8, cursor: "default", opacity: busy ? 0.6 : 1 }}>
              <button style={{ background: "transparent", border: "none", color: "inherit", cursor: "pointer", fontFamily: "inherit", fontSize: 12, padding: 0 }}
                aria-pressed={sp.active !== false}
                title={sp.active !== false ? "Active — click to deactivate" : "Inactive — click to activate"}
                disabled={!isAdmin || busy} onClick={() => toggle(sp.key)}>
                {sp.active !== false ? "● " : "○ "}{sp.label}
              </button>
              {isAdmin && sp.key !== "other" && (
                <button style={{ background: "transparent", border: "none", color: color.text.secondary, cursor: "pointer", fontSize: 11, padding: 0 }}
                  title="Rename" onClick={() => startEdit(sp)}>✎</button>
              )}
              {isAdmin && sp.custom && (
                <button style={{ background: "transparent", border: "none", color: color.accent.error, cursor: "pointer", fontSize: 12, padding: 0 }}
                  title="Remove" onClick={() => removeCustom(sp)}>×</button>
              )}
            </span>
          )
        ))}
        {isAdmin && (
          <button style={s.locAdd} onClick={addCustom} disabled={busy}>＋ Add Custom</button>
        )}
      </div>
      <p style={{ fontSize: 12, color: color.text.muted }}>
        Specialties group the surgeon list and fill the surgeon "specialty" dropdown. Facility-specific.
      </p>
    </div>
  );
}
